import { SetShowtimeErrorCodeV1, SetShowtimeErrorV1 } from '@vista-digital/ocapi-client';
import { runInAction } from 'mobx';
import { ocapiClient } from '../../../../api-clients/ocapi/client';
import { CommonErrorCodes } from '../../../../types/errors/common-error-codes';
import { logger } from '../../../../utils/logger';
import { nonEmptyStringValidator } from '../../../../utils/validators/string';
import { loadSeatAvailabilityForShowtime } from '../../../seating/actions/load-seat-availability-for-showtime';
import { OrderDomainError, OrderDomainSpecificErrorCode } from '../../errors/order-domain-error';
import { mapOrderTicketLineToOrderTicketRequest } from '../../mappers/order-ticket-line';
import { getOrder } from '../../selectors/order/get-order';
import { getOrderSelections } from '../../selectors/order/get-order-selections';
import { orderDomainStore } from '../../store';
import { assertIsStandardOrder } from '../../utils/order-type-utils';
import { setOrderSeatsForShowtime } from '../seats/set-order-seats-for-showtime';
import { registerOrderAction } from '../utils/register-order-action';
import { syncOrder } from '../utils/sync-order';
export const saveOrderShowtime = async (showtimeId) => {
    const order = getOrder();
    assertIsStandardOrder(order, 'Cannot save showtime');
    if (!nonEmptyStringValidator(showtimeId, logger.error, 'showtimeId')) {
        throw new Error('Cannot save showtime. Provided showtimeId is invalid.');
    }
    const orderSelections = getOrderSelections();
    if (!orderSelections.showtime || orderSelections.showtime.showtimeId !== showtimeId) {
        logger.debug(`Cannot save showtime. Selections for showtime '${showtimeId}' are already in sync with the API.`);
        return;
    }
    const deregisterAction = registerOrderAction('save-order-showtime-selections');
    try {
        const response = await ocapiClient.instance.v1.orders.showtimes.set(order.id, showtimeId, getRequest(orderSelections.showtime));
        syncOrder(response);
    }
    catch (error) {
        throw await handleApiError(order.id, showtimeId, error);
    }
    finally {
        deregisterAction();
    }
    runInAction(() => {
        delete orderDomainStore.data.selections.showtime;
    });
};
const getRequest = (selections) => {
    return {
        seats: selections.seats.map(seat => seat.seatId),
        tickets: selections.tickets.map(mapOrderTicketLineToOrderTicketRequest)
    };
};
const handleApiError = async (orderId, showtimeId, error) => {
    if (!(error instanceof SetShowtimeErrorV1))
        throw error; // Unexpected error, rethrow immediately
    const errorCode = convertApiToDomainErrorCode(error.errorCode);
    // Seats are no longer available. Clear selections and reload seat availability
    if (errorCode === OrderDomainSpecificErrorCode.SeatsUnavailable) {
        await loadSeatAvailabilityForShowtime(showtimeId, orderId);
        setOrderSeatsForShowtime(showtimeId, []);
    }
    return new OrderDomainError(errorCode, error.message);
};
const convertApiToDomainErrorCode = (errorCode) => {
    switch (errorCode) {
        case SetShowtimeErrorCodeV1.SeatsUnavailable:
            return OrderDomainSpecificErrorCode.SeatsUnavailable;
        default:
            return CommonErrorCodes.UnknownError;
    }
};
